// Self-signups › Automatic emails › Follow-up after signup (owner, November 4: "an automatic follow-up
// ... 2 hours after they sign up ... If they sign up at like 10 p.m., then we
// should send it in the morning ... Don't make it like a promotional email").
// One email to everyone who signs up on scorelm.com, a template the owner picks
// (by default their own "Thanks for signing up"), signed by a person, plain:
// no tracked links, no unsubscribe footer, and the logo when Signature and
// footer's Show the logo is on. Off until the owner turns
// it on here, and turning it on is the approval, as scheduling a campaign is.
// Signups before that get none. Turning it off takes the ones not yet sent off
// the Schedule. The wait after signup is Ops's to set, 2 hours by default
// (owner, November 4: "can we make that time configurable ... under automatic
// emails"); a new wait counts for signups from then on. The server decides when
// each goes (services/growth/signup-follow-up.ts); this card only says the rule.
// What really goes is in contact@scorelm.com's Gmail, under Sent Mail: the
// owner's way to see it go out (November 4), since we send as that account.

import { useEffect, useState } from 'react'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import type { FollowUpView } from '@/api/growthCampaignTypes'
import { Badge, Button, ButtonLink, Card, CardHeader, ConfirmDialog, ErrorState, FormField, InlineAlert, LoadingState, SelectInput, useToast } from '@/components/ui'
import { formatDate } from '@/lib/format'
import { invalidateQueries, setQueryData, useQuery } from '@/lib/query'

const KEY = ['growth', 'signup-follow-up']

/** The waits offered. One saved that isn't here (set through the API) is offered too. */
const WAITS = [15, 30, 60, 120, 180, 240, 360, 720, 1440]

/** "15 minutes", "1 hour", "2 hours", "1 hour 30 minutes". */
function waitLabel(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const hours = h === 0 ? '' : h === 1 ? '1 hour' : `${h} hours`
  const mins = m === 0 ? '' : m === 1 ? '1 minute' : `${m} minutes`
  return [hours, mins].filter(Boolean).join(' ') || '0 minutes'
}

function hour(h: number): string {
  if (h === 0 || h === 24) return 'midnight'
  if (h === 12) return 'noon'
  return h < 12 ? `${h} am` : `${h - 12} pm`
}

/** The rule in words, from the server's numbers. */
function ruleText(rule: FollowUpView['rule']): string {
  return `${waitLabel(rule.delay_minutes)} after they sign up, if that's between ${hour(rule.day_start)} and ${hour(rule.day_end)} their time. Later than that, it waits for ${hour(rule.day_start)}. If we don't know their time zone, it goes between 8 am Pacific and ${hour(rule.day_end)} Eastern.`
}

export function FollowUpCard() {
  const toast = useToast()
  const view = useQuery(KEY, (signal) => api.growth.templates.followUp(signal))
  const options = useQuery(['signups', 'outreach-options'], (signal) => api.growth.outreach.options(signal))
  const [templateId, setTemplateId] = useState('')
  const [signer, setSigner] = useState('')
  const [delay, setDelay] = useState('')
  const [dialog, setDialog] = useState<'on' | 'off' | null>(null)
  const [saving, setSaving] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const data = view.data
  useEffect(() => {
    if (!data) return
    setTemplateId(data.settings.template_id ? String(data.settings.template_id) : '')
    setSigner(data.settings.signer)
    setDelay(String(data.settings.delay_minutes))
  }, [data])

  const save = async (enabled: boolean) => {
    setSaving(true)
    setProblem(null)
    try {
      const next = await api.growth.templates.saveFollowUp({ enabled, template_id: Number(templateId), signer, delay_minutes: Number(delay) })
      setQueryData(KEY, next)
      invalidateQueries('growth')
      toast.success(enabled ? (data?.settings.enabled ? 'Saved. Follow-ups from now on use it.' : 'Turned on. Everyone who signs up from now on gets it.') : 'Turned off. Nothing more goes.')
      return next
    } catch (err) {
      setProblem(errorMessage(err))
      throw err
    } finally {
      setSaving(false)
    }
  }

  const on = !!data?.settings.enabled
  const changed =
    !!data && (templateId !== String(data.settings.template_id ?? '') || signer !== data.settings.signer || delay !== String(data.settings.delay_minutes))
  const waits = data && !WAITS.includes(data.settings.delay_minutes) ? [...WAITS, data.settings.delay_minutes].sort((a, b) => a - b) : WAITS
  const dryRun = options.data?.sending.mode === 'dry_run'

  return (
    <Card>
      <CardHeader
        title="Follow-up after signup"
        description="One email to everyone who signs up on scorelm.com, from ScoreLM <contact@scorelm.com>, signed by a person. No tracked links and no unsubscribe footer, so it reads like a note, not a promotion. The logo shows when Show the logo is on in Signature and footer."
        actions={data ? <Badge tone={on ? 'good' : 'neutral'}>{on ? 'On' : 'Off'}</Badge> : undefined}
      />
      {view.status === 'loading' && !data ? (
        <LoadingState label="Loading the follow-up…" />
      ) : !data ? (
        <ErrorState error={view.error} title="Couldn't load the follow-up" onRetry={view.refetch} />
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-1">
            <h3 className="text-[13px] font-bold text-ink">When it goes</h3>
            <p data-testid="follow-up-rule" className="text-[13px] text-muted">{ruleText(data.rule)}</p>
            {on && data.settings.enabled_at ? (
              <p className="text-xs text-subtle">On since {formatDate(data.settings.enabled_at)}. Signups before then get none.</p>
            ) : (
              <p className="text-xs text-subtle">Only people who sign up after you turn it on get one.</p>
            )}
          </div>

          <div className="grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <FormField label="Template it sends" hint="Edit its words like any template.">
              {({ id, describedBy }) => (
                <SelectInput
                  id={id}
                  aria-describedby={describedBy}
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  options={[
                    ...(templateId ? [] : [{ value: '', label: 'Pick a template' }]),
                    ...(options.data?.templates ?? (data.template ? [{ id: data.template.id, name: data.template.name }] : [])).map((t) => ({ value: String(t.id), label: t.name })),
                  ]}
                />
              )}
            </FormField>
            <FormField label="Signed by">
              {({ id, describedBy }) => (
                <SelectInput
                  id={id}
                  aria-describedby={describedBy}
                  value={signer}
                  onChange={(e) => setSigner(e.target.value)}
                  options={(options.data?.signers ?? []).map((s) => ({ value: s.key, label: s.title ? `${s.name}, ${s.title}` : s.name }))}
                />
              )}
            </FormField>
            <FormField label="Wait after signup" hint="Then the 8 am to 8 pm rule. Ones already on the Schedule keep their time.">
              {({ id, describedBy }) => (
                <SelectInput
                  id={id}
                  aria-describedby={describedBy}
                  value={delay}
                  onChange={(e) => setDelay(e.target.value)}
                  options={waits.map((m) => ({ value: String(m), label: waitLabel(m) }))}
                />
              )}
            </FormField>
          </div>

          {data.sample && !changed ? (
            <div className="grid gap-1">
              <h3 className="text-[13px] font-bold text-ink">What a signup gets</h3>
              <div className="grid gap-2 rounded-md bg-raised p-3">
                <p className="text-[13px] font-semibold text-ink" data-testid="follow-up-subject">{data.sample.subject}</p>
                <pre data-testid="follow-up-sample" className="whitespace-pre-wrap break-words font-sans text-[13px] text-ink">{data.sample.text}</pre>
              </div>
              <p className="text-xs text-subtle">A sample signup: Jordan Smith, Riverside Hoops.</p>
            </div>
          ) : changed ? (
            <p className="text-xs text-muted">Save to see the email as it will go.</p>
          ) : null}

          {!changed && data.problem ? <InlineAlert tone="warn">{data.problem}</InlineAlert> : null}
          {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
          {on && dryRun ? (
            <InlineAlert tone="info">Sending is off on this server, so each follow-up is recorded as a dry run at its time, and nothing leaves.</InlineAlert>
          ) : null}
          <p className="text-xs text-muted">
            To see one go out: each that really goes is in Gmail, in contact@scorelm.com’s Sent Mail, where their replies land too.
          </p>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-muted">
              {data.scheduled > 0 ? (
                <>
                  {data.scheduled} on the <ButtonLink to="/growth/schedule" size="sm" variant="ghost">Schedule</ButtonLink>, not sent yet.
                </>
              ) : on ? 'None waiting to go.' : null}
            </span>
            <div className="flex flex-wrap gap-2">
              {changed ? (
                <Button onClick={() => void save(on).catch(() => undefined)} loading={saving} disabled={!templateId}>
                  Save
                </Button>
              ) : null}
              {on ? (
                <Button onClick={() => setDialog('off')} disabled={saving}>
                  Turn off…
                </Button>
              ) : (
                <Button variant="primary" onClick={() => setDialog('on')} disabled={saving || !templateId}>
                  Turn on…
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={dialog === 'on'}
        onClose={() => setDialog(null)}
        title="Turn on the follow-up after signup?"
        description="From now on, everyone who signs up on scorelm.com gets this email once, at the time above. People who signed up before now get none."
        confirmLabel="Turn on"
        tone="primary"
        onConfirm={async () => {
          await save(true)
          setDialog(null)
        }}
      />
      <ConfirmDialog
        open={dialog === 'off'}
        onClose={() => setDialog(null)}
        title="Turn off the follow-up after signup?"
        description={data && data.scheduled > 0 ? `The ${data.scheduled} not sent yet come off the Schedule, and nothing more goes.` : 'Nothing more goes.'}
        confirmLabel="Turn off"
        tone="danger"
        onConfirm={async () => {
          await save(false)
          setDialog(null)
        }}
      />
    </Card>
  )
}
